import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { ReactComponent as DotsIcon } from '../../resources/icons/dots.svg';
import { ReactComponent as UserIcon } from '../../resources/icons/user.svg';
import { ReactComponent as ProjectsIcon } from '../../resources/icons/projects.svg';
import { ReactComponent as NavDot } from '../../resources/icons/nav-dot.svg';
import NotificationsIcon from '../../resources/icons/notifications.svg';
import downArrow from '../../resources/down_arrow.png';
import Logo from '../../resources/icons/logo.png';

import { preventCachedImage } from '../../utils/helperFunctions';
import NotificationsModal from './modals/NotificationsModal';

import { useNotificationsContext } from '../../contexts/notificationsContext';
import { User } from '../../types';
import { useMyProjects } from '../../hooks/useMyProjects';


interface Props {
  user: User;
}

const ClientSidebar = ({ user }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isProjectsOpen, setIsProjectsOpen] = useState(false);
  const [activeProjectId, setActiveProjectId] = useState<number | null>(null);

  const { projects, fetchProjects } = useMyProjects();

  const [showNotificationsModal, setShowNotificationsModal] = useState(false);

  const { notifications } = useNotificationsContext();
  const unreadCount = notifications.filter(
    (n) => n.status !== 'RECEIVED'
  ).length;

  useEffect(() => {
    fetchProjects(1, 30);
  }, [fetchProjects]);

  useEffect(() => {
    const path = location.pathname;

    if (path.startsWith('/projects/')) {
      const projectId = parseInt(path.split('/projects/')[1]);
      if (!isNaN(projectId)) {
        setActiveProjectId(projectId);
        setIsProjectsOpen(true);
      }
    } else if (path === '/projects') {
      setActiveProjectId(null);
      setIsProjectsOpen(true);
    } else {
      setActiveProjectId(null);
      setIsProjectsOpen(false);
    }
  }, [location.pathname]);

  const handleToggleProjects = () => {
    const isOnProjectsPage = location.pathname === '/projects';
    const isOnSingleProjectPage = location.pathname.startsWith('/projects/');

    if (isOnSingleProjectPage) {
      navigate('/projects');
      setIsProjectsOpen(true);
      setActiveProjectId(null);
    } else if (isOnProjectsPage) {
      setIsProjectsOpen(!isProjectsOpen);
      setActiveProjectId(null);
    } else {
      navigate('/projects');
      setIsProjectsOpen(true);
      setActiveProjectId(null);
    }
  };

  const handleProjectClick = (projectId: number) => {
    const project = projects.find((p) => p.id === projectId);

    if (!project) return;

    setActiveProjectId(projectId);

    if (project.status === 'COMPLETED' || project.status === 'CANCELLED') {
      navigate(`/projects/${projectId}/finished`);
    } else {
      const userType = localStorage.getItem('userType');
      if (userType === 'ASSIGNOR') {
        navigate(`/projects/${projectId}/publish`);
      } else {
        navigate(`/projects/${projectId}/edit`);
      }
    }
  };

  const getDotColor = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return '#98A2B3';
      case 'OPEN':
        return '#1672EC';
      case 'ONGOING':
        return '#FC6A19';
      case 'CANCELLED':
        return '#B42318';
      case 'COMPLETED':
        return '#067647';
      default:
        return '#344054';
    }
  };

  return (
    <aside className="fixed left-0 top-0 w-[17.5rem] h-screen bg-[#202F3D] flex flex-col border-r border-[#D0D5DD] z-50">
      <button
        className="h-[4.5rem] p-6 flex items-center gap-2 border-b border-[#D0D5DD] text-white"
        onClick={handleToggleProjects}
      >
        <span>
          <img src={Logo} alt="Logo" />
        </span>
        <h2 className="text-xl font-bold">Build Connect</h2>
      </button>

      <nav className="flex-1 overflow-y-auto px-[1.5rem] pt-[1rem] text-white">
        <div className="mb-4">
          <button
            onClick={handleToggleProjects}
            className={`flex items-center cursor-pointer py-1 px-2 rounded transition-colors w-full hover:bg-[#364450] ${
              location.pathname === '/projects' ? 'bg-[#364450]' : ''
            }`}
          >
            <ProjectsIcon className="w-5 h-5 mr-2" />
            <span className="font-medium">My projects</span>
            <img
              src={downArrow}
              alt="Toggle dropdown"
              className={`w-5 h-5 ml-auto transform transition-transform filter brightness-0 invert ${
                isProjectsOpen ? 'rotate-180' : ''
              }`}
            />
          </button>

          {isProjectsOpen && (
            <div className="mt-2 flex flex-col gap-2">
              {projects.map((project) => (
                <button
                  key={project.id}
                  onClick={() => handleProjectClick(project.id)}
                  className={`flex items-center px-6 py-1 rounded transition-colors overflow-hidden ${
                    activeProjectId === project.id ? 'bg-[#364450]' : ''
                  }`}
                >
                  <NavDot
                    className="self-start mr-3 mt-2 flex-shrink-0"
                    style={{ color: getDotColor(project.status) }}
                  />
                  <span
                    className={`${
                      activeProjectId === project.id ? '' : 'truncate'
                    } max-w-[12rem] text-left`}
                  >
                    {project.title}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={() => setShowNotificationsModal(true)}
          className="flex items-center cursor-pointer py-1 px-2 hover:bg-[#364450] rounded relative w-full"
        >
          <img
            src={NotificationsIcon}
            alt="Notifications"
            className="w-5 h-5 mr-2"
          />
          <span className="text-white font-medium">Notifications</span>

          {unreadCount > 0 && unreadCount < 10 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold w-5 h-5 p-1 rounded-full flex justify-center items-center">
              {unreadCount}
            </span>
          )}
          {unreadCount > 0 && unreadCount >= 10 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold w-5 h-5 p-1 rounded-full flex justify-center items-center">
              9+
            </span>
          )}
        </button>
      </nav>

      <button
        className={`m-4 pt-3 text-start hover:bg-[#364450] hover:rounded-md ${
          location.pathname.includes('my-profile')
            ? 'bg-[#364450] rounded-md'
            : 'border-t border-[#364450] rounded-none'
        }`}
        onClick={() => navigate('/my-profile')}
      >
        <div className="p-2 flex items-start justify-between text-white max-w-md">
          <div className="flex items-center">
            {user.image ? (
              <img
                src={preventCachedImage(user.image)}
                alt="Profile"
                className="w-12 h-12 object-cover mr-2 rounded-full"
              />
            ) : (
              <UserIcon className="mr-2" />
            )}
            <div>
              <div className="text-white font-medium truncate max-w-[10rem]">
                {user.name}
              </div>
              <div className="text-sm text-white truncate max-w-[10rem]">
                {user.email}
              </div>
            </div>
          </div>

          <DotsIcon className="w-5 h-5 cursor-pointer" />
        </div>
      </button>

      <NotificationsModal
        isOpen={showNotificationsModal}
        onClose={() => setShowNotificationsModal(false)}
      />
    </aside>
  );
};

export default ClientSidebar;
